// iOS Safari only lets an AudioContext enter the "running" state from inside a
// user gesture. Video compression runs later, from a timer, with no gesture on
// the stack — so an AudioContext created there stays "suspended" and a <video>
// routed through it never plays to completion (the transcode hangs forever).
//
// To avoid that we keep a single shared AudioContext and "unlock" it on the
// first user interaction with the page. By the time the user has picked files
// to upload they have already tapped, so the context is running when the
// transcoder needs it.

let sharedContext: AudioContext | null = null
let unlockInstalled = false

const getAudioContextCtor = (): typeof AudioContext | null => {
  if (typeof window === "undefined") return null
  return (window as any).AudioContext || (window as any).webkitAudioContext || null
}

export const getSharedAudioContext = (): AudioContext | null => {
  const Ctor = getAudioContextCtor()
  if (!Ctor) return null
  if (!sharedContext) {
    try {
      sharedContext = new Ctor()
    } catch {
      return null
    }
  }
  return sharedContext
}

const resumeAndUnlock = () => {
  const ctx = getSharedAudioContext()
  if (!ctx) return
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {})
  }
  // Playing a one-sample silent buffer is the canonical trick that reliably
  // flips iOS Safari's AudioContext to "running" inside a gesture.
  try {
    const buffer = ctx.createBuffer(1, 1, 22050)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.start(0)
  } catch {}
}

/**
 * Install one-time listeners that unlock the shared AudioContext on the first
 * user interaction. Safe to call repeatedly — it only installs once.
 */
export const installAudioUnlock = (): void => {
  if (unlockInstalled || typeof window === "undefined") return
  unlockInstalled = true

  const events = ["pointerdown", "touchend", "click", "keydown"]
  const handler = () => {
    resumeAndUnlock()
    events.forEach(e => window.removeEventListener(e, handler))
  }
  events.forEach(e => window.addEventListener(e, handler))
}
